import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, Paper, IconButton, Tooltip,
    CircularProgress, Typography, Chip, Divider,
    Button as MuiButton,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';

const emptyRow = () => ({
    _id: Date.now() + Math.random(),
    boxBarCode: '',
    noOfItems: '',
    weightKg: '',
    boxDimension: '',
    boxDescription: '',
    isNew: true,
});

const COLS = [
    { key: 'boxBarCode', label: 'Barcode', editable: true, width: 150 },
    { key: 'noOfItems', label: 'Qty', editable: true, width: 80 },
    { key: 'weightKg', label: 'Weight (Kg)', editable: true, width: 100 },
    { key: 'boxDimension', label: 'Dimensions', editable: true, width: 130 },
    { key: 'boxDescription', label: 'Description', editable: true, width: 180 },
];

const GoodsReceivedItemsModal = ({ open, onClose, onConfirm, orderData }) => {
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [fetched, setFetched] = useState(false);

    useEffect(() => {
        if (open && orderData?.orderId) {
            fetchItems(orderData.orderId);
        }
        // reset on close
        if (!open) { setItems([]); setFetched(false); }
    }, [open, orderData?.orderId]);

    const fetchItems = async (orderId) => {
        try {
            setLoading(true);
            const res = await PndRestApi.GetOrderItemWiseDetails(orderId);
            if (res.status === 200 && res.data.status === 200 && Array.isArray(res.data.data) && res.data.data.length > 0) {
                setItems(res.data.data.map(i => ({ ...i, _id: Date.now() + Math.random(), isNew: false })));
            } else {
                // No items yet — start with one blank row
                setItems([emptyRow()]);
            }
        } catch (e) {
            console.error('Item fetch error:', e);
            showNotification('theme-light', 'ltr', 'Error', 'Failed to load order items.');
            setItems([emptyRow()]);
        } finally {
            setLoading(false);
            setFetched(true);
        }
    };

    const addRow = () => setItems(prev => [...prev, emptyRow()]);

    const removeRow = (id) => setItems(prev => prev.filter(r => r._id !== id));

    const handleChange = (id, field, value) => {
        setItems(prev => prev.map(r => r._id === id ? { ...r, [field]: value } : r));
    };

    const handleConfirm = () => {
        // Pass items back to parent so it can include them in API call if needed
        onConfirm(items);
    };

    const inputStyle = {
        border: '1px solid #d1d5db',
        borderRadius: 4,
        padding: '4px 6px',
        fontSize: '0.8rem',
        width: '100%',
        outline: 'none',
        background: '#fff',
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: 'visible' } }}>

            {/* ── Header ── */}
            <DialogTitle sx={{ p: 0 }}>
                <div style={{
                    background: 'linear-gradient(135deg, #064e3b 0%, #10b981 100%)',
                    borderTopLeftRadius: 12, borderTopRightRadius: 12,
                    padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <InventoryIcon sx={{ color: '#fff', fontSize: '1.6rem' }} />
                    <div style={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.2 }}>
                            Goods Received — Order Items
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                            Order: <b>{orderData?.orderNumber || '—'}</b>
                            {orderData?.fromName ? ` · From: ${orderData.fromName}` : ''}
                        </Typography>
                    </div>
                    <IconButton onClick={onClose} size="small" sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent sx={{ p: 2, pt: 2 }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
                        <CircularProgress color="success" size={36} />
                        <Typography sx={{ ml: 2, color: '#64748b' }}>Loading order items…</Typography>
                    </div>
                ) : (
                    <>
                        {/* Info bar */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Chip
                                    size="small"
                                    label={`${items.length} item${items.length !== 1 ? 's' : ''}`}
                                    sx={{ background: '#d1fae5', color: '#065f46', fontWeight: 700, fontSize: '0.75rem' }}
                                />
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Review existing items below. You can add new rows if needed.
                                </Typography>
                            </div>
                            <Tooltip title="Add new item row" arrow>
                                <MuiButton
                                    size="small"
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={addRow}
                                    sx={{
                                        textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
                                        color: '#10b981', borderColor: '#10b981',
                                        '&:hover': { background: '#d1fae5' },
                                    }}
                                    variant="outlined"
                                >
                                    Add Row
                                </MuiButton>
                            </Tooltip>
                        </div>

                        <Divider sx={{ mb: 1.5 }} />

                        <TableContainer component={Paper} variant="outlined"
                            sx={{ maxHeight: 380, border: '1px solid #d1fae5', borderRadius: 2 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={thStyle}>#</TableCell>
                                        {COLS.map(c => (
                                            <TableCell key={c.key} sx={{ ...thStyle, minWidth: c.width }}>{c.label}</TableCell>
                                        ))}
                                        <TableCell sx={{ ...thStyle, width: 50 }}>Del</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((row, idx) => (
                                        <TableRow key={row._id}
                                            sx={{ background: row.isNew ? '#f0fdf4' : (idx % 2 === 0 ? '#fff' : '#f8fafc') }}>
                                            <TableCell sx={{ fontSize: '0.78rem', color: '#64748b' }}>{idx + 1}</TableCell>
                                            {COLS.map(col => (
                                                <TableCell key={col.key} sx={{ p: '4px 6px' }}>
                                                    <input
                                                        style={{ ...inputStyle, borderColor: row.isNew ? '#6ee7b7' : '#d1d5db' }}
                                                        value={row[col.key] ?? ''}
                                                        onChange={e => handleChange(row._id, col.key, e.target.value)}
                                                        placeholder={col.label}
                                                        disabled={!col.editable && !row.isNew}
                                                    />
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ p: '4px 6px', textAlign: 'center' }}>
                                                <Tooltip title="Remove row" arrow>
                                                    <IconButton size="small" onClick={() => removeRow(row._id)}
                                                        sx={{ color: '#ef4444', '&:hover': { background: '#fee2e2' } }}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>

            <Divider />
            <DialogActions sx={{ p: '12px 16px', gap: 1 }}>
                <MuiButton
                    onClick={onClose}
                    variant="outlined" size="small"
                    sx={{ textTransform: 'none', borderColor: '#94a3b8', color: '#64748b', '&:hover': { background: '#f1f5f9' } }}
                >
                    Cancel
                </MuiButton>
                <MuiButton
                    onClick={handleConfirm}
                    variant="contained" size="small"
                    disabled={loading}
                    startIcon={<CheckCircleOutlineIcon />}
                    sx={{
                        textTransform: 'none', fontWeight: 700,
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
                        boxShadow: '0 2px 8px rgba(16,185,129,0.35)',
                    }}
                >
                    Confirm Goods Received
                </MuiButton>
            </DialogActions>
        </Dialog>
    );
};

const thStyle = {
    backgroundColor: '#059669',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
    py: 1,
};

export default GoodsReceivedItemsModal;
